import { useState, useCallback, useEffect, useRef } from 'react';
import { GameState, Book, Hero, CollectibleObject, HomeObject, BackgroundConfig, HeroConfig, LogoConfig, UserItemInstance, StoryProgress } from '../types';
import useLocalStorage from './useLocalStorage';
import { useHeroes } from './useHeroes';
import { useObjects } from './useObjects';
import { useEnhancedObjects } from './useEnhancedObjects';
import { useUserProfile } from './useUserProfile';
import { supabase } from '../lib/supabaseClient';
import { toCamelCase } from '../utils/caseConversion';
import { useSyncService } from './useSyncService';

interface UseGameStateOptions {
  childId?: number | null;
}

const getInitialGameState = () => ({
  homeObjects: [], // Vide par défaut, sera rempli depuis Supabase ou manuellement par l'utilisateur
  heroConfig: { x: 50, y: 50, scale: 0.25, rotation: 0 }, // Scale 25% avec baseSize 384px = 96px visuel
  backgroundConfig: { gradient: 'sky-purple' as const, intensity: 'normal' as const },
  logoConfig: { color: null }, // null = auto (basé sur le gradient)
  selectedHero: null, // Sera défini automatiquement au premier héros disponible par défaut
  collectedObjects: [], // Vide par défaut, sera rempli en terminant des livres
  completedBooks: []
});

export const useGameState = (options: UseGameStateOptions = {}) => {
  const { childId } = options;

  // Get user info for multi-user localStorage support
  const { user, isAuthenticated } = useUserProfile();
  const userId = user?.id; // undefined if guest

  // Sync service for offline support and retry logic
  const { enqueue: enqueueMutation, isOnline, isSyncing, hasPending } = useSyncService({
    userId,
    childId,
    enabled: !!userId
  });

  // Storage key includes childId for per-child data
  const storageKeySuffix = childId ? `_child${childId}` : '';

  // Load data from APIs
  const { heroes: apiHeroes, loading: heroesLoading } = useHeroes();
  const { objects: gameObjects, loading: objectsLoading } = useObjects();

  // Objects are already in the correct format from useObjects
  const apiObjects = gameObjects;
  
  // États temporaires (non persistés)
  const [currentScreen, setCurrentScreen] = useState<GameState['currentScreen']>('home');
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [showReward, setShowReward] = useState(false);
  const [newReward, setNewReward] = useState<CollectibleObject | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState('apercu');
  const [isPremium, setIsPremium] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState<GameState['selectedElement']>(null);

  // États persistés (avec support multi-utilisateurs et multi-enfants)
  const initialGameState = getInitialGameState();
  const [homeObjects, setHomeObjects] = useLocalStorage<HomeObject[]>(`homeObjects${storageKeySuffix}`, initialGameState.homeObjects, userId);
  const [heroConfig, setHeroConfig] = useLocalStorage<HeroConfig>(`heroConfig${storageKeySuffix}`, initialGameState.heroConfig, userId);
  const [backgroundConfig, setBackgroundConfig] = useLocalStorage<BackgroundConfig>(`backgroundConfig${storageKeySuffix}`, initialGameState.backgroundConfig, userId);
  const [logoConfig, setLogoConfig] = useLocalStorage<LogoConfig>(`logoConfig${storageKeySuffix}`, initialGameState.logoConfig, userId);
  const [selectedHero, setSelectedHeroInternal] = useLocalStorage<number | null>(`selectedHero${storageKeySuffix}`, initialGameState.selectedHero, userId);
  const [userInstances, setUserInstances] = useLocalStorage<UserItemInstance[]>(`userInstances${storageKeySuffix}`, [], userId);
  const [completedBooks, setCompletedBooks] = useLocalStorage<number[]>(`completedBooks${storageKeySuffix}`, initialGameState.completedBooks, userId);
  const [storyProgress, setStoryProgress] = useLocalStorage<StoryProgress | null>(`storyProgress${storageKeySuffix}`, null, userId);

  // Alias for setSelectedHero - avatar sync is handled by DB trigger on user_world_config
  const setSelectedHero = setSelectedHeroInternal;

  // Backward compatibility: générer collectedObjects depuis userInstances
  // C'est la liste des element IDs (avec duplicates pour instances multiples)
  const collectedObjects = userInstances
    .filter(instance => !instance.archived)
    .map(instance => instance.baseElementId);

  // Helper function to save data to Supabase via sync service
  // Uses the offline queue with retry logic
  const saveToSupabase = useCallback((dataType: 'world_config' | 'home_objects' | 'collection' | 'story_progress', data: any) => {
    if (!user || !userId) {
      console.log('⏭️ Skip Supabase save (guest mode)');
      return;
    }

    switch (dataType) {
      case 'world_config':
        // Enqueue for sync (will use atomic RPC)
        enqueueMutation('world_config', {
          heroConfig: data.heroConfig,
          backgroundConfig: data.backgroundConfig,
          selectedHeroId: data.selectedHeroId
        });
        console.log('📤 World config queued for sync');
        break;

      case 'home_objects':
        // Enqueue for sync (will use atomic RPC)
        enqueueMutation('home_objects', data);
        console.log(`📤 ${data.length} home objects queued for sync`);
        break;

      case 'collection':
        // DEPRECATED: Les instances sont maintenant sauvegardées directement
        // dans completeBook() et transformObject() avec INSERT
        console.log('⚠️ saveToSupabase("collection") is deprecated. Instances are saved directly.');
        break;

      case 'story_progress':
        // Enqueue for sync
        enqueueMutation('story_progress', data);
        console.log(`📤 ${data.length} completed stories queued for sync`);
        break;
    }
  }, [user, userId, enqueueMutation]);

  // Charger les données depuis Supabase quand l'utilisateur est authentifié
  useEffect(() => {
    if (!userId) return; // Mode guest ou pas encore chargé, rester avec localStorage

    const loadFromSupabase = async () => {
      try {
        console.log('🔄 Chargement des données depuis Supabase pour user:', userId, 'child:', childId);

        // 1. Charger la configuration du monde
        let worldConfigQuery = supabase
          .from('user_world_config')
          .select('*')
          .eq('user_id', userId);

        if (childId) {
          worldConfigQuery = worldConfigQuery.eq('child_id', childId);
        } else {
          worldConfigQuery = worldConfigQuery.is('child_id', null);
        }

        const { data: worldConfig, error: worldConfigError } = await worldConfigQuery.maybeSingle();

        if (worldConfigError) {
          console.warn('⚠️ Erreur chargement world_config (ignorée):', worldConfigError.message);
        }

        if (worldConfig) {
          const worldConfigCamelCase = toCamelCase(worldConfig);
          setHeroConfig(worldConfigCamelCase.heroConfig || initialGameState.heroConfig);
          setBackgroundConfig(worldConfigCamelCase.backgroundConfig || initialGameState.backgroundConfig);
          setSelectedHero(worldConfigCamelCase.selectedHeroId || initialGameState.selectedHero);
          console.log('✅ Configuration du monde chargée');
        }

        // 2. Charger les objets de la maison
        let homeObjectsQuery = supabase
          .from('user_home_objects')
          .select('*')
          .eq('user_id', userId);

        if (childId) {
          homeObjectsQuery = homeObjectsQuery.eq('child_id', childId);
        } else {
          homeObjectsQuery = homeObjectsQuery.is('child_id', null);
        }

        const { data: homeObjectsData } = await homeObjectsQuery;

        if (homeObjectsData && homeObjectsData.length > 0) {
          const homeObjectsDataCamelCase = toCamelCase(homeObjectsData);
          const transformedHomeObjects: HomeObject[] = homeObjectsDataCamelCase.map((obj: any) => ({
            id: obj.id,
            objectId: obj.elementId,
            instanceId: obj.instanceId, // NEW: Load instanceId from DB
            x: obj.x,
            y: obj.y,
            scale: obj.scale,
            rotation: obj.rotation
          }));
          setHomeObjects(transformedHomeObjects);
          console.log(`✅ ${transformedHomeObjects.length} objets de maison chargés`);
        } else {
          setHomeObjects([]);
        }

        // 3. Charger toutes les instances d'objets (depuis user_item_instances)
        let instancesQuery = supabase
          .from('user_item_instances')
          .select('*')
          .eq('user_id', userId);

        if (childId) {
          instancesQuery = instancesQuery.eq('child_id', childId);
        } else {
          instancesQuery = instancesQuery.is('child_id', null);
        }

        const { data: instancesData } = await instancesQuery;

        if (instancesData && instancesData.length > 0) {
          const instancesDataCamelCase = toCamelCase(instancesData);
          const instances: UserItemInstance[] = instancesDataCamelCase.map((item: any) => ({
            id: item.id,
            userId: item.userId,
            baseElementId: item.baseElementId,
            appliedEffects: item.appliedEffects || [],
            sourceType: item.sourceType || 'earned',
            craftedFrom: item.craftedFrom,
            archived: item.archived || false,
            archivedAt: item.archivedAt,
            acquiredAt: item.acquiredAt
          }));
          setUserInstances(instances);
          console.log(`✅ ${instances.length} instances d'objets chargées (${instances.filter(i => !i.archived).length} actives)`);
        } else {
          setUserInstances([]);
        }

        // 4. Charger la progression des histoires (complétées)
        let progressQuery = supabase
          .from('story_progress')
          .select('story_id')
          .eq('user_id', userId)
          .eq('completed', true);

        if (childId) {
          progressQuery = progressQuery.eq('child_id', childId);
        } else {
          progressQuery = progressQuery.is('child_id', null);
        }

        const { data: progressData } = await progressQuery;

        if (progressData && progressData.length > 0) {
          const progressDataCamelCase = toCamelCase(progressData);
          const storyIds = progressDataCamelCase.map((item: any) => item.storyId);
          setCompletedBooks(storyIds);
          console.log(`✅ ${storyIds.length} histoires complétées chargées`);
        } else {
          setCompletedBooks([]);
        }

        // 5. Charger la progression de lecture en cours (non complétée)
        let inProgressQuery = supabase
          .from('story_progress')
          .select('story_id, current_page, visited_pages, last_read_at')
          .eq('user_id', userId)
          .eq('completed', false)
          .order('last_read_at', { ascending: false })
          .limit(1);

        if (childId) {
          inProgressQuery = inProgressQuery.eq('child_id', childId);
        } else {
          inProgressQuery = inProgressQuery.is('child_id', null);
        }

        const { data: inProgressData } = await inProgressQuery;

        // Comparer avec localStorage et décider qui gagne
        const localProgressKey = `lilhero_storyProgress${storageKeySuffix}_${userId}`;
        const localProgressRaw = localStorage.getItem(localProgressKey);
        const localProgress: StoryProgress | null = localProgressRaw ? JSON.parse(localProgressRaw) : null;

        const serverProgress = inProgressData && inProgressData.length > 0
          ? toCamelCase(inProgressData[0])
          : null;

        // Décider quelle progression utiliser
        if (localProgress && serverProgress) {
          // Les deux existent: comparer les dates
          const localDate = new Date(localProgress.startedAt || 0).getTime();
          const serverDate = new Date(serverProgress.lastReadAt || 0).getTime();

          if (localDate > serverDate) {
            // Local plus récent → garder local et pousser vers serveur
            console.log('📤 Local progress is newer, pushing to server');
            setStoryProgress(localProgress);
            // Push async (fire and forget)
            supabase.from('story_progress').upsert({
              user_id: userId,
              child_id: childId || null,
              story_id: localProgress.storyId,
              current_page: localProgress.currentPageKey,
              visited_pages: localProgress.allVisitedPages,
              completed: false,
              last_read_at: localProgress.startedAt
            }, { onConflict: 'user_id,child_id,story_id' });
          } else {
            // Serveur plus récent → utiliser serveur
            console.log('📥 Server progress is newer, using server data');
            const restoredProgress: StoryProgress = {
              storyId: serverProgress.storyId,
              currentPageKey: serverProgress.currentPage || '',
              pageHistory: [],
              allVisitedPages: serverProgress.visitedPages || [],
              startedAt: serverProgress.lastReadAt || new Date().toISOString()
            };
            setStoryProgress(restoredProgress);
          }
        } else if (localProgress && !serverProgress) {
          // Seulement local → pousser vers serveur
          console.log('📤 Only local progress exists, pushing to server');
          setStoryProgress(localProgress);
          supabase.from('story_progress').upsert({
            user_id: userId,
            child_id: childId || null,
            story_id: localProgress.storyId,
            current_page: localProgress.currentPageKey,
            visited_pages: localProgress.allVisitedPages,
            completed: false,
            last_read_at: localProgress.startedAt
          }, { onConflict: 'user_id,child_id,story_id' });
        } else if (serverProgress && !localProgress) {
          // Seulement serveur → restaurer
          console.log('📥 Only server progress exists, restoring');
          const restoredProgress: StoryProgress = {
            storyId: serverProgress.storyId,
            currentPageKey: serverProgress.currentPage || '',
            pageHistory: [],
            allVisitedPages: serverProgress.visitedPages || [],
            startedAt: serverProgress.lastReadAt || new Date().toISOString()
          };
          setStoryProgress(restoredProgress);
        }

        console.log('✅ Toutes les données Supabase chargées avec succès');
      } catch (error) {
        console.error('❌ Erreur lors du chargement depuis Supabase:', error);
        // En cas d'erreur, garder les données localStorage
      }
    };

    loadFromSupabase();
  }, [userId, childId]); // Charger seulement quand userId (string) ou childId change

  // Auto-save to Supabase when data changes (authenticated users only)
  useEffect(() => {
    if (!user || !userId) return;

    // Debounce to avoid too many saves
    const timer = setTimeout(() => {
      saveToSupabase('world_config', {
        heroConfig,
        backgroundConfig,
        selectedHeroId: selectedHero
      });
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroConfig, backgroundConfig, selectedHero, user, userId]);

  useEffect(() => {
    if (!user || !userId) return;

    const timer = setTimeout(() => {
      saveToSupabase('home_objects', homeObjects);
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeObjects, user, userId]);

  // DEPRECATED: Ne plus sauvegarder collectedObjects automatiquement
  // Les instances sont maintenant sauvegardées directement dans completeBook() et transformObject()
  // useEffect(() => {
  //   if (!user || !userId) return;
  //   const timer = setTimeout(() => {
  //     saveToSupabase('collection', collectedObjects);
  //   }, 1000);
  //   return () => clearTimeout(timer);
  // }, [collectedObjects, user, userId]);

  useEffect(() => {
    if (!user || !userId) return;

    const timer = setTimeout(() => {
      saveToSupabase('story_progress', completedBooks);
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedBooks, user, userId]);

  // Use API data instead of static data
  const heroes = apiHeroes;

  // Auto-initialisation : créer les instances pour les héros unlocked au premier login
  useEffect(() => {
    const initializeDefaultHeroes = async () => {
      // Seulement pour users authentifiés
      if (!user || !userId) return;

      // Seulement si aucune collection existe (premier login)
      if (collectedObjects.length > 0) return;

      // Seulement si les héros sont chargés
      if (heroes.length === 0) return;

      const unlockedHeroes = heroes.filter(h => h.unlocked);

      if (unlockedHeroes.length === 0) {
        console.warn('⚠️ Aucun héros débloqué par défaut trouvé');
        return;
      }

      console.log(`🎁 Initialisation : ajout de ${unlockedHeroes.length} héros par défaut`);

      try {
        // Vérifier quels héros existent déjà pour éviter les duplicates
        const { data: existingInstances } = await supabase
          .from('user_item_instances')
          .select('base_element_id')
          .eq('user_id', userId);

        const existingElementIds = (existingInstances || []).map((inst: any) => inst.base_element_id);

        // Ne créer que les héros qui n'existent pas encore
        const heroesToCreate = unlockedHeroes.filter(h => !existingElementIds.includes(h.id));

        if (heroesToCreate.length === 0) {
          console.log('✅ Tous les héros par défaut existent déjà');
          return;
        }

        const defaultInstances = heroesToCreate.map(h => ({
          user_id: userId,
          base_element_id: h.id,
          source_type: 'earned',
          acquired_at: new Date().toISOString(),
        }));

        const { error } = await supabase
          .from('user_item_instances')
          .insert(defaultInstances);

        if (error) {
          console.error('❌ Erreur lors de l\'initialisation des héros par défaut:', error);
        } else {
          console.log(`✅ ${heroesToCreate.length} héros par défaut ajoutés à la DB`);

          // Recharger les instances depuis Supabase
          const { data: instancesData } = await supabase
            .from('user_item_instances')
            .select('*')
            .eq('user_id', userId);

          if (instancesData) {
            const instancesDataCamelCase = toCamelCase(instancesData);
            const instances: UserItemInstance[] = instancesDataCamelCase.map((item: any) => ({
              id: item.id,
              userId: item.userId,
              baseElementId: item.baseElementId,
              appliedEffects: item.appliedEffects || [],
              sourceType: item.sourceType || 'earned',
              craftedFrom: item.craftedFrom,
              archived: item.archived || false,
              archivedAt: item.archivedAt,
              acquiredAt: item.acquiredAt
            }));
            setUserInstances(instances);
          }
        }
      } catch (err) {
        console.error('❌ Erreur inattendue lors de l\'initialisation:', err);
      }
    };

    initializeDefaultHeroes();
  }, [user, userId, heroes, userInstances.length, setUserInstances]);

  // Auto-sélectionner le premier héros débloqué si aucun héros n'est sélectionné
  useEffect(() => {
    if (!selectedHero && heroes.length > 0) {
      // Pour users authentifiés: prendre le premier de la collection
      // Pour guests: prendre le premier unlocked
      const firstHero = user
        ? heroes.find(h => collectedObjects.includes(h.id))
        : heroes.find(h => h.unlocked);

      if (firstHero) {
        console.log('🦸 Auto-sélection du premier héros:', firstHero.id);
        setSelectedHero(firstHero.id);
      }
    }
  }, [heroes, selectedHero, setSelectedHero, user, collectedObjects]);

  // Utiliser le hook pour les objets avec effets
  const { allObjects } = useEnhancedObjects({
    baseObjects: apiObjects,
    userInstances
  });

  // Actions
  // Result type for completeBook
  interface CompleteBookResult {
    success: boolean;
    alreadyCompleted: boolean;
    rewardsGranted: boolean;
    rewardsCount?: number;
    message?: string;
  }

  const completeBook = useCallback(async (book: Book, rewardIds?: number[]): Promise<CompleteBookResult> => {
    const bookObjects = rewardIds || book.objects || [];
    console.log('💾 Completing story:', book.id, 'rewards:', bookObjects);

    // Mode guest ou pas de user: local only
    if (!userId) {
      if (!completedBooks.includes(book.id)) {
        setCompletedBooks(prev => [...prev, book.id]);
      }
      return { success: true, alreadyCompleted: false, rewardsGranted: true, rewardsCount: bookObjects.length };
    }

    // Mode connecté: utiliser le RPC atomique
    try {
      const { data, error } = await supabase.rpc('complete_story', {
        p_user_id: userId,
        p_story_id: book.id,
        p_child_id: childId || null,
        p_reward_element_ids: bookObjects
      });

      if (error) {
        console.error('❌ Erreur RPC complete_story:', error);
        // Fallback: sauvegarder localement en cas d'erreur réseau
        if (!completedBooks.includes(book.id)) {
          setCompletedBooks(prev => [...prev, book.id]);
        }
        return { success: false, alreadyCompleted: false, rewardsGranted: false, message: error.message };
      }

      console.log('📦 Complete story result:', data);

      // Mettre à jour l'état local
      if (!completedBooks.includes(book.id)) {
        setCompletedBooks(prev => [...prev, book.id]);
      }

      // Si récompenses accordées, recharger les instances
      if (data.rewards_granted && data.rewards_count > 0) {
        let reloadQuery = supabase
          .from('user_item_instances')
          .select('*')
          .eq('user_id', userId);

        if (childId) {
          reloadQuery = reloadQuery.eq('child_id', childId);
        } else {
          reloadQuery = reloadQuery.is('child_id', null);
        }

        const { data: instancesData } = await reloadQuery;

        if (instancesData) {
          const instancesDataCamelCase = toCamelCase(instancesData);
          const instances: UserItemInstance[] = instancesDataCamelCase.map((item: any) => ({
            id: item.id,
            userId: item.userId,
            baseElementId: item.baseElementId,
            appliedEffects: item.appliedEffects || [],
            sourceType: item.sourceType || 'earned',
            craftedFrom: item.craftedFrom,
            archived: item.archived || false,
            archivedAt: item.archivedAt,
            acquiredAt: item.acquiredAt
          }));
          setUserInstances(instances);
          console.log(`✅ ${instances.length} instances rechargées`);
        }
      }

      return {
        success: data.success,
        alreadyCompleted: data.already_completed || false,
        rewardsGranted: data.rewards_granted || false,
        rewardsCount: data.rewards_count || 0,
        message: data.message
      };

    } catch (err: any) {
      console.error('❌ Exception complete_story:', err);
      // Fallback local
      if (!completedBooks.includes(book.id)) {
        setCompletedBooks(prev => [...prev, book.id]);
      }
      return { success: false, alreadyCompleted: false, rewardsGranted: false, message: err.message };
    }
  }, [completedBooks, setCompletedBooks, userId, childId, setUserInstances]);

  const addObjectToHome = useCallback((instanceId: number): HomeObject | null => {
    // Trouver l'instance dans userInstances
    const instance = userInstances.find(inst => inst.id === instanceId);
    if (!instance || instance.archived) {
      console.error(`⚠️ Instance ${instanceId} non trouvée ou archivée`);
      return null;
    }

    // Vérifier que cette instance n'est pas déjà placée
    const alreadyPlaced = homeObjects.some(obj => obj.instanceId === instanceId);
    if (alreadyPlaced) {
      console.log(`⚠️ Cette instance est déjà placée dans le monde`);
      return null;
    }

    const newItem: HomeObject = {
      id: Date.now(),
      objectId: instance.baseElementId, // Keep for backward compat
      instanceId: instanceId, // The real reference
      x: 20 + Math.random() * 60,
      y: 20 + Math.random() * 60,
      scale: 0.25, // 25% de la taille de base (384px) = 96px visuel
      rotation: Math.random() * 360
    };
    setHomeObjects(prev => [...prev, newItem]);

    console.log(`✅ Instance ${instanceId} (element ${instance.baseElementId}) ajoutée au monde`);

    return newItem;
  }, [userInstances, homeObjects, setHomeObjects]);

  const removeObjectFromHome = useCallback((id: number) => {
    setHomeObjects(prev => prev.filter(obj => obj.id !== id));
  }, [setHomeObjects]);

  const updateHomeObject = useCallback((id: number, updates: Partial<HomeObject>) => {
    setHomeObjects(prev => prev.map(obj => 
      obj.id === id ? { ...obj, ...updates } : obj
    ));
  }, [setHomeObjects]);

  const updateHeroConfig = useCallback((updates: Partial<HeroConfig>) => {
    setHeroConfig(prev => ({ ...prev, ...updates }));
  }, [setHeroConfig]);

  const transformObject = useCallback(async (targetInstanceId: number, specialInstanceId: number) => {
    if (!userId) {
      alert('❌ Vous devez être connecté pour transformer des objets');
      return;
    }

    // Trouver les instances directement par leur ID
    const targetInstance = userInstances.find(inst => inst.id === targetInstanceId && !inst.archived);
    const specialInstance = userInstances.find(inst => inst.id === specialInstanceId && !inst.archived);

    if (!targetInstance || !specialInstance) {
      console.error('❌ Instances non trouvées:', { targetInstanceId, specialInstanceId });
      alert('❌ Impossible de trouver les instances. Rechargez la page.');
      return;
    }

    const originalObject = allObjects.find(obj => obj.id === targetInstance.baseElementId);
    const specialObject = allObjects.find(obj => obj.id === specialInstance.baseElementId);

    if (!originalObject || !specialObject) {
      console.error('❌ Éléments non trouvés:', { targetInstance, specialInstance });
      return;
    }

    console.log(`🌟 Transforming ${originalObject.name} (instance: ${targetInstanceId}) with ${specialObject.name} (instance: ${specialInstanceId})`);

    // Vérifier que l'objet spécial a des effets configurés
    const grantedEffects = (specialObject as any).grantedEffects || [];
    if (grantedEffects.length === 0) {
      console.error('❌ Aucun effet configuré pour l\'objet spécial:', specialObject.name);
      alert(`❌ L'objet spécial "${specialObject.name}" n'a pas d'effet configuré !`);
      return;
    }

    try {
      // Appeler la fonction Supabase transform_item
      const { data, error } = await supabase.rpc('transform_item', {
        p_target_instance_id: targetInstance.id,
        p_special_instance_id: specialInstance.id
      });

      if (error) {
        console.error('❌ Erreur lors de la transformation:', error);
        alert(`❌ Erreur: ${error.message}`);
        return;
      }

      console.log('✅ Transformation réussie:', data);

      // Retirer l'objet spécial consommé du monde (homeObjects) s'il y était placé
      setHomeObjects(prev => {
        const filtered = prev.filter(obj => obj.instanceId !== specialInstance.id);
        if (filtered.length < prev.length) {
          console.log(`🗑️ Objet spécial retiré du monde (instanceId: ${specialInstance.id})`);
        }
        return filtered;
      });

      // Recharger les instances depuis Supabase
      const { data: instancesData } = await supabase
        .from('user_item_instances')
        .select('*')
        .eq('user_id', userId);

      if (instancesData) {
        const instancesDataCamelCase = toCamelCase(instancesData);
        const instances: UserItemInstance[] = instancesDataCamelCase.map((item: any) => ({
          id: item.id,
          userId: item.userId,
          baseElementId: item.baseElementId,
          appliedEffects: item.appliedEffects || [],
          sourceType: item.sourceType || 'earned',
          craftedFrom: item.craftedFrom,
          archived: item.archived || false,
          archivedAt: item.archivedAt,
          acquiredAt: item.acquiredAt
        }));
        setUserInstances(instances);
        console.log(`✅ Instances rechargées: ${instances.length} total`);
      }

      alert(`🎉 Transformation réussie ! ${originalObject.name} ✨ a été transformé avec ${grantedEffects.join(', ')} !`);
    } catch (err) {
      console.error('❌ Erreur inattendue:', err);
      alert('❌ Erreur lors de la transformation');
    }
  }, [allObjects, userInstances, userId, setUserInstances, setHomeObjects]);

  // Story progress management
  const saveStoryProgress = useCallback((progress: StoryProgress) => {
    // Sauvegarder localement (immédiat)
    setStoryProgress(progress);
  }, [setStoryProgress]);

  // Sync story progress to server (called on exit reading screen)
  const syncStoryProgressToServer = useCallback(async (progress: StoryProgress | null) => {
    if (!progress || !userId) return;

    try {
      const { error } = await supabase
        .from('story_progress')
        .upsert({
          user_id: userId,
          child_id: childId || null,
          story_id: progress.storyId,
          current_page: progress.currentPageKey,
          visited_pages: progress.allVisitedPages,
          completed: false,
          last_read_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,child_id,story_id'
        });

      if (error) {
        console.error('❌ Error syncing story progress:', error);
      } else {
        console.log('✅ Story progress synced to server');
      }
    } catch (err) {
      console.error('❌ Exception syncing story progress:', err);
    }
  }, [userId, childId]);

  const clearStoryProgress = useCallback((storyId: number) => {
    if (storyProgress && storyProgress.storyId === storyId) {
      setStoryProgress(null);
    }
  }, [storyProgress, setStoryProgress]);

  const resetGame = useCallback(() => {
    setHomeObjects(initialGameState.homeObjects);
    setHeroConfig(initialGameState.heroConfig);
    setBackgroundConfig(initialGameState.backgroundConfig);
    setLogoConfig(initialGameState.logoConfig);
    setSelectedHero(initialGameState.selectedHero);
    setUserInstances([]); // Vider les instances
    setCompletedBooks(initialGameState.completedBooks);
    setStoryProgress(null); // Clear story progress
    setCurrentScreen('home');
    setSelectedBook(null);
    setCurrentPage(0);
    setEditMode(false);
    setSelectedElement(null);
    // Note: Heroes will be reset to API default state on next fetch
  }, [setHomeObjects, setHeroConfig, setBackgroundConfig, setLogoConfig, setSelectedHero, setUserInstances, setCompletedBooks, setStoryProgress]);

  const gameState: GameState = {
    currentScreen,
    selectedBook,
    currentPage,
    showReward,
    newReward,
    audioEnabled,
    activeTab,
    isPremium,
    homeObjects,
    heroConfig,
    backgroundConfig,
    logoConfig,
    editMode,
    selectedElement,
    heroes,
    selectedHero,
    collectedObjects,
    completedBooks,
    storyProgress,
    userInstances
  };

  return {
    gameState,
    allObjects,
    userInstances,
    // Loading states
    loading: heroesLoading || objectsLoading,
    // Sync status (for UI indicators)
    syncStatus: {
      isOnline,
      isSyncing,
      hasPending
    },
    // Setters pour les états temporaires
    setCurrentScreen,
    setSelectedBook,
    setCurrentPage,
    setShowReward,
    setNewReward,
    setAudioEnabled,
    setActiveTab,
    setIsPremium,
    setEditMode,
    setSelectedElement,
    // Setters pour les états persistés
    setHomeObjects,
    setHeroConfig,
    setBackgroundConfig,
    setLogoConfig,
    setSelectedHero,
    setUserInstances,
    setCompletedBooks,
    // Actions
    completeBook,
    addObjectToHome,
    removeObjectFromHome,
    updateHomeObject,
    updateHeroConfig,
    transformObject,
    saveStoryProgress,
    syncStoryProgressToServer,
    clearStoryProgress,
    resetGame
  };
};

export default useGameState;